import { useState, useMemo, useRef, useCallback } from "react";
import libroService from "../../../../services/libroService";
import ModeracionService from "../../../../services/ModeracionService";
import TranslateService from "../../../../services/TranslateService";
import { TRADUCTOR_CONFIG } from "../config/traductor.config";

const {
  LANGUAGES,
  MAX_WORDS,
  MAX_FILE_SIZE_MB,
  ALLOWED_EXTENSIONS,
} = TRADUCTOR_CONFIG;

export const useTraductorLogic = () => {
  const [texto, setTexto] = useState("");
  const [traduccion, setTraduccion] = useState("");
  const [idiomaOrigen, setIdiomaOrigen] = useState("es");
  const [idiomaDestino, setIdiomaDestino] = useState("en");
  const [archivoNombre, setArchivoNombre] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);

  const fileInputRef = useRef(null);

  const [snack, setSnack] = useState({
    open: false,
    severity: "warning",
    message: "",
  });

  const showSnack = (message, severity = "warning") => {
    setSnack({ open: true, severity, message });
  };

  const closeSnack = (_, reason) => {
    if (reason === "clickaway") return;
    setSnack((p) => ({ ...p, open: false }));
  };

  const palabras = useMemo(() => {
    if (!texto.trim()) return 0;
    return texto.trim().split(/\s+/).length;
  }, [texto]);

  const textoExcedeLimite = palabras > MAX_WORDS;

  const getModeracionMessage = (err) => {
    const detail = err?.response?.data?.detail;
    return detail?.message || "Contenido no permitido (malas palabras o +18).";
  };

  const validarTexto = async (contenido) => {
    try {
      await ModeracionService.validarContenido({
        tema: "TRADUCTOR",
        objetivo: contenido || "",
        sub_area_tematica: "TRADUCTOR",
      });
      return true;
    } catch (err) {
      showSnack(getModeracionMessage(err), "error");
      return false;
    }
  };

  const handleTextoChange = useCallback((e) => {
    setTexto(e.target.value);
    setTraduccion("");
  }, []);

  const limpiarCampos = () => {
    setTexto("");
    setTraduccion("");
    setArchivoNombre("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleTraducir = async () => {
    if (!texto.trim()) return;

    if (textoExcedeLimite) {
      showSnack(`El texto excede el límite de ${MAX_WORDS} palabras.`, "warning");
      return;
    }

    const ok = await validarTexto(texto);
    if (!ok) return;

    setLoading(true);
    try {
      const res = await TranslateService.traducirTexto(
        texto,
        idiomaOrigen,
        idiomaDestino
      );
      setTraduccion(res.mainTranslation);
    } catch (err) {
      console.error(err);
      showSnack("Error al traducir el texto.", "error");
      setTraduccion("⚠️ Error al traducir el texto.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      showSnack(`El archivo supera los ${MAX_FILE_SIZE_MB} MB.`, "warning");
      return;
    }

    const extension = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      showSnack("Solo se permiten archivos PDF, DOC, DOCX o TXT.", "warning");
      event.target.value = null;
      return;
    }

    setArchivoNombre(file.name);
    setFileLoading(true);

    try {
      const textoExtraido = await libroService.SubirArchivo(file);

      if (!textoExtraido?.trim()) {
        showSnack("No se pudo extraer texto del archivo.", "error");
        return;
      }

      const palabrasArray = textoExtraido.trim().split(/\s+/);
      const contenidoFinal =
        palabrasArray.length > MAX_WORDS
          ? palabrasArray.slice(0, MAX_WORDS).join(" ")
          : textoExtraido;

      const ok = await validarTexto(contenidoFinal);
      if (!ok) {
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setTexto(contenidoFinal);

      if (palabrasArray.length > MAX_WORDS) {
        showSnack(
          `El documento excede el límite. Solo se cargaron las primeras ${MAX_WORDS} palabras.`,
          "warning"
        );
      } else {
        showSnack("Archivo cargado correctamente.", "success");
      }
    } catch (err) {
      console.error(err);
      showSnack("No se pudo procesar el archivo.", "error");
    } finally {
      setFileLoading(false);
    }
  };

  const copiarTexto = async () => {
    if (!traduccion) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(traduccion);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = traduccion;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      showSnack("Traducción copiada.", "success");
    } catch (err) {
      console.error(err);
      showSnack("No se pudo copiar el texto.", "error");
    }
  };

  const actualizarDestinoAlCambiarOrigen = (nuevoOrigen) => {
    if (nuevoOrigen === idiomaDestino) {
      const alternativa = LANGUAGES.find((i) => i.code !== nuevoOrigen);
      if (alternativa) setIdiomaDestino(alternativa.code);
    }
  };

  const intercambiarIdiomas = () => {
    const nuevoOrigen = idiomaDestino;
    const nuevoDestino = idiomaOrigen;

    const nuevoTexto = traduccion;
    const nuevaTraduccion = texto;

    setIdiomaOrigen(nuevoOrigen);
    setIdiomaDestino(nuevoDestino);
    setTexto(nuevoTexto);
    setTraduccion(nuevaTraduccion);
  };

  return {
    texto,
    traduccion,
    idiomaOrigen,
    idiomaDestino,
    archivoNombre,
    loading,
    fileLoading,
    snack,
    palabras,
    textoExcedeLimite,
    fileInputRef,

    setIdiomaOrigen,
    setIdiomaDestino,

    handleTextoChange,
    handleTraducir,
    handleFileUpload,
    intercambiarIdiomas,
    limpiarCampos,
    copiarTexto,
    actualizarDestinoAlCambiarOrigen,
    closeSnack,
  };
};
